import { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';

import { KnownExperiments, defaultExperimentSet } from './experiments.ts';

/**
 * Derive the subcategory from a pathname by stripping the locale prefix
 * and/or experiment segment (if present), returning the first meaningful
 * content path segment.
 *
 * URL patterns this handles (as seen by usePathname in the browser):
 *   /[experiments]/[...path]  → e.g. /control/sponsor-a-child
 *   /[...path]                → e.g. /sponsor-a-child
 *   /[locale]/[...path]       → e.g. /en/sponsor-a-child (compex, preview)
 *
 * The experiment segment is identified positionally (index 0, or index 1
 * when a locale is at index 0) and validated against the known experiment
 * names and the underscore-joined encoding pattern.
 */
export const deriveSubcategory = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const locales: Set<string> = new Set(LOCALES_CODES.options);

  let startIndex = 0;

  // Strip locale at position 0 if present
  if (segments[0] && locales.has(segments[0])) {
    startIndex = 1;
  }

  // Strip experiment set at the current position (whether preceded by
  // a locale or not). An experiment segment is either the default
  // ('control') or composed of known experiment keys joined by underscores.
  if (segments[startIndex]) {
    const candidate = segments[startIndex];
    const isExperiment =
      candidate === defaultExperimentSet ||
      candidate
        .split('_')
        .every((part) => KnownExperiments.safeParse(part).success);
    if (isExperiment) {
      startIndex += 1;
    }
  }

  return segments[startIndex] || '';
};
